import { getZodiacSign } from '../utils/zodiac'
import { supabase } from './supabase'
import { uploadPhoto } from './storage'

/**
 * Сохранить все данные онбординга в профиль пользователя
 * @param {string} userId - ID пользователя
 * @param {Object} onboardingData - Данные из онбординга
 */
export async function saveOnboardingToProfile(userId, onboardingData) {
  try {
    // Проверяем обязательные поля
    if (!onboardingData.name) {
      throw new Error('Name is required')
    }
    if (!onboardingData.gender) {
      throw new Error('Gender is required')
    }
    if (!onboardingData.lookingFor) {
      throw new Error('Looking for is required')
    }
    if (!onboardingData.birthYear || !onboardingData.birthMonth || !onboardingData.birthDay) {
      throw new Error('Date of birth is required')
    }
    if (!onboardingData.photos || !Array.isArray(onboardingData.photos) || onboardingData.photos.length === 0) {
      throw new Error('At least one photo is required')
    }

    // Маппинг lookingFor: 'men' → 'male', 'women' → 'female', 'both' → 'both'
    let lookingFor = onboardingData.lookingFor
    if (lookingFor === 'men') {
      lookingFor = 'male'
    } else if (lookingFor === 'women') {
      lookingFor = 'female'
    } else if (lookingFor !== 'both' && lookingFor !== 'male' && lookingFor !== 'female') {
      // Если значение не соответствует ожидаемым, используем 'both' по умолчанию
      console.warn(`[saveOnboardingToProfile] Invalid lookingFor value: ${lookingFor}, using 'both'`)
      lookingFor = 'both'
    }

    // Вычисляем знак зодиака
    let zodiacSign = null
    if (onboardingData.birthDay && onboardingData.birthMonth) {
      const zodiac = getZodiacSign(onboardingData.birthDay, onboardingData.birthMonth)
      zodiacSign = zodiac.name
    }

    // Формируем дату рождения (обязательное поле)
    const dateOfBirth = `${onboardingData.birthYear}-${String(onboardingData.birthMonth).padStart(2, '0')}-${String(onboardingData.birthDay).padStart(2, '0')}`

    // Формируем время рождения (если есть)
    let timeOfBirth = null
    if (onboardingData.birthTime) {
      timeOfBirth = onboardingData.birthTime
    }

    // Загружаем фотографии в Supabase Storage (с таймаутом для каждой)
    const photoUrls = []
    const photoErrors = []
    if (onboardingData.photos && Array.isArray(onboardingData.photos)) {
      console.log(`[saveOnboardingToProfile] Starting upload of ${onboardingData.photos.length} photos...`)
      
      for (let i = 0; i < onboardingData.photos.length; i++) {
        const photo = onboardingData.photos[i]
        if (photo) {
          try {
            // Таймаут увеличен до 30 секунд для файлов до 10MB
            const uploadPromise = uploadPhoto(photo, userId, i)
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Photo upload timeout (30s)')), 30000)
            })
            
            const url = await Promise.race([uploadPromise, timeoutPromise])
            if (url) {
              photoUrls.push(url)
              console.log(`[saveOnboardingToProfile] ✅ Photo ${i + 1}/${onboardingData.photos.length} uploaded`)
            }
          } catch (error) {
            console.error(`[saveOnboardingToProfile] ❌ Error uploading photo ${i}:`, error)
            photoErrors.push({ index: i, error: error.message })
            // Продолжаем загрузку остальных фото
          }
        }
      }
      
      console.log(`[saveOnboardingToProfile] Upload complete: ${photoUrls.length}/${onboardingData.photos.length} photos uploaded`)
    }

    // Проверяем, что хотя бы одна фотография загрузилась
    if (photoUrls.length === 0) {
      throw new Error(`Failed to upload photos: ${photoErrors.map(e => e.error).join(', ')}`)
    }

    // Обновляем профиль (все обязательные поля должны быть заполнены)
    const updates = {
      name: onboardingData.name,
      gender: onboardingData.gender,
      looking_for: lookingFor,
      date_of_birth: dateOfBirth,
      photos: photoUrls,
    }

    // Добавляем необязательные поля только если они есть
    if (timeOfBirth) {
      updates.time_of_birth = timeOfBirth
    }
    if (onboardingData.birthPlace) {
      updates.place_of_birth = onboardingData.birthPlace
    }
    if (zodiacSign) {
      updates.zodiac_sign = zodiacSign
    }
    if (onboardingData.interests && Array.isArray(onboardingData.interests) && onboardingData.interests.length > 0) {
      updates.interests = onboardingData.interests
    }
    if (onboardingData.termsAcceptedAt) {
      updates.terms_accepted_at = onboardingData.termsAcceptedAt
    }
    if (onboardingData.termsVersion != null && onboardingData.termsVersion !== '') {
      updates.terms_version = String(onboardingData.termsVersion)
    }

    console.log('[saveOnboardingToProfile] Saving profile with data:', {
      userId,
      updates: Object.keys(updates),
      name: updates.name,
      gender: updates.gender,
      looking_for: updates.looking_for,
      date_of_birth: updates.date_of_birth,
      photosCount: updates.photos?.length || 0
    })

    // Проверяем, существует ли профиль и заполнен ли он
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id, name, gender, looking_for, date_of_birth')
      .eq('id', userId)
      .single()

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('[saveOnboardingToProfile] Error checking profile:', checkError)
      throw checkError
    }

    let result
    if (!existingProfile || (!existingProfile.name && !existingProfile.gender)) {
      // Профиль не существует или пустой - используем INSERT (upsert)
      console.log('[saveOnboardingToProfile] Profile is empty, using upsert...')
      
      // Получаем email из auth.users
      const { data: userData } = await supabase.auth.getUser()
      const email = userData?.user?.email || ''
      
      // Добавляем email и id для INSERT
      const insertData = {
        id: userId,
        email: email,
        ...updates
      }

      const { data, error } = await supabase
        .from('profiles')
        .upsert(insertData, { onConflict: 'id' })
        .select()
        .single()

      if (error) {
        console.error('[saveOnboardingToProfile] Upsert error:', error)
        throw error
      }

      result = { data, error: null }
    } else {
      // Профиль существует - используем UPDATE
      console.log('[saveOnboardingToProfile] Profile exists, using update...')
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()

      if (error) {
        console.error('[saveOnboardingToProfile] Update error:', error)
        throw error
      }

      result = { data, error: null }
    }

    console.log('[saveOnboardingToProfile] ✅ Profile saved successfully:', {
      id: result.data?.id,
      name: result.data?.name,
      gender: result.data?.gender,
      looking_for: result.data?.looking_for,
      date_of_birth: result.data?.date_of_birth,
      photosCount: result.data?.photos?.length || 0
    })

    return result
  } catch (error) {
    console.error('Error saving onboarding to profile:', error)
    return { data: null, error: error.message }
  }
}
